/**
 * MISMO navigation panels.
 * Panels open on click. Close with the close button, Esc, the scrim or a click outside.
 * Tile videos play while their panel is open and pause when it closes.
 */
class MismoNav extends HTMLElement {
  /** @type {HTMLElement | null} */
  #openTrigger = null;
  /** @type {HTMLElement | null} */
  #openPanel = null;
  #lockOwner = null;
  #lockFns = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('pointerdown', this.#onPointerDown, true);
    window.addEventListener('resize', this.#onResize);
    this.addEventListener('focusout', this.#onFocusOut);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('pointerdown', this.#onPointerDown, true);
    window.removeEventListener('resize', this.#onResize);
    this.removeEventListener('focusout', this.#onFocusOut);
    this.#unlock();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {Element} */ (event.target);
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-mismo-trigger]');
    if (trigger && this.contains(trigger)) {
      const id = trigger.getAttribute('data-mismo-trigger');
      if (this.#openTrigger === trigger) {
        this.close(true);
      } else if (id) {
        this.open(/** @type {HTMLElement} */ (trigger), id);
      }
      return;
    }

    if (target.closest('[data-mismo-close]') || target.matches('[data-mismo-scrim]')) {
      this.close(true);
      return;
    }

    const tab = target.closest('[data-mismo-tab]');
    if (tab) {
      this.#selectTab(/** @type {HTMLElement} */ (tab));
      return;
    }

    // A link inside a panel was used. The page changes, so close the panel.
    if (target.closest('.mismo-panel a')) this.close(false);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape' && this.#openPanel) {
      event.preventDefault();
      this.close(true);
    }
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (!this.#openPanel) return;
    const target = event.target;
    if (!(target instanceof Node)) return;
    if (this.#openPanel.contains(target)) return;
    if (this.#openTrigger && this.#openTrigger.contains(target)) return;
    // Clicks on other triggers are handled by the click handler.
    if (target instanceof Element && target.closest('[data-mismo-trigger]') && this.contains(target)) return;
    this.close(false);
  };

  #onResize = () => {
    if (!this.#openPanel) return;
    if (window.innerWidth < 990) {
      this.close(false);
    } else {
      this.#place();
    }
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (!this.#openPanel) return;
    const next = /** @type {Node | null} */ (event.relatedTarget);
    if (next && !this.contains(next)) this.close(false);
  };

  /**
   * Find the bottom edge of the visible header, in viewport pixels.
   * The header component box can be shorter than its rows, so measure the rows and the logo too.
   */
  #headerBottom() {
    const header = document.querySelector('#header-component') || document.querySelector('header');
    if (!header) return 80;
    let bottom = header.getBoundingClientRect().bottom;
    header.querySelectorAll('.header__row, .header-logo').forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.height > 0) bottom = Math.max(bottom, r.bottom);
    });
    const trigger = this.#openTrigger;
    if (trigger) bottom = Math.max(bottom, trigger.getBoundingClientRect().bottom);
    return Math.round(bottom);
  }

  /**
   * Place the scrim and the panel under the header.
   * The header uses "contain: layout", so fixed boxes would position against the header.
   * Absolute boxes are measured from 0,0 and then moved to the wanted spot instead.
   * The panel left edge sits under the clicked link and stays inside the page.
   */
  #place() {
    const panel = this.#openPanel;
    const trigger = this.#openTrigger;
    const scrim = this.querySelector('[data-mismo-scrim]');
    if (!panel || !trigger) return;

    const headerBottom = this.#headerBottom();
    const viewW = document.documentElement.clientWidth;
    const gap = 24;

    // Put a box at a wanted viewport position. Measure from 0,0 so the real containing block is used.
    /** @param {HTMLElement} el @param {number} top @param {number} left */
    const put = (el, top, left) => {
      el.style.animation = 'none';
      el.style.position = 'absolute';
      el.style.top = '0px';
      el.style.left = '0px';
      const r = el.getBoundingClientRect();
      el.style.top = `${top - r.top}px`;
      el.style.left = `${left - r.left}px`;
      el.style.animation = '';
    };

    if (scrim instanceof HTMLElement) {
      scrim.style.width = `${viewW}px`;
      scrim.style.height = `${Math.max(0, window.innerHeight - headerBottom)}px`;
      put(scrim, headerBottom, 0);
    }

    const width = panel.offsetWidth;
    const tr = trigger.getBoundingClientRect();
    const wanted = Math.min(Math.max(tr.left, gap), Math.max(gap, viewW - width - gap));

    panel.style.transform = 'none';
    panel.style.right = 'auto';
    panel.style.maxHeight = `${Math.max(240, window.innerHeight - headerBottom - 24)}px`;
    put(panel, headerBottom + 8, wanted);
  }

  /** Play the videos that show in the open panel. Pause all others. */
  #syncVideos() {
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.querySelectorAll('video').forEach((v) => {
      if (!(v instanceof HTMLVideoElement)) return;
      v.muted = true;
      v.loop = true;
      v.setAttribute('playsinline', '');
      const visible = !!this.#openPanel && this.#openPanel.contains(v) && v.offsetParent !== null;
      if (visible && !reduce) {
        const p = v.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } else {
        v.pause();
      }
    });
  }

  /**
   * @param {HTMLElement} trigger
   * @param {string} panelId
   */
  open(trigger, panelId) {
    const panel = this.querySelector(`#${CSS.escape(panelId)}`);
    if (!(panel instanceof HTMLElement)) return;

    this.#hideAll();

    const scrim = this.querySelector('[data-mismo-scrim]');
    if (scrim instanceof HTMLElement) scrim.hidden = false;

    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    this.#openTrigger = trigger;
    this.#openPanel = panel;
    this.#place();
    this.#syncVideos();

    this.#lock();

    // Move focus into the panel: the selected tab if there is one, otherwise the close button.
    const focusTarget =
      panel.querySelector('[role="tab"][aria-selected="true"]') || panel.querySelector('[data-mismo-close]');
    if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
  }

  /** @param {boolean} returnFocus */
  close(returnFocus) {
    const trigger = this.#openTrigger;
    this.#hideAll();
    this.#openTrigger = null;
    this.#openPanel = null;
    this.#syncVideos();
    this.#unlock();
    if (returnFocus && trigger) trigger.focus({ preventScroll: true });
  }

  #hideAll() {
    this.querySelectorAll('[data-mismo-panel]').forEach((p) => {
      if (p instanceof HTMLElement) p.hidden = true;
    });
    this.querySelectorAll('[data-mismo-trigger]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
    const scrim = this.querySelector('[data-mismo-scrim]');
    if (scrim instanceof HTMLElement) scrim.hidden = true;
  }

  /** @param {HTMLElement} tab */
  #selectTab(tab) {
    const panel = tab.closest('[data-mismo-panel]');
    if (!panel) return;
    const name = tab.getAttribute('data-mismo-tab');
    panel.querySelectorAll('[data-mismo-tab]').forEach((t) => {
      t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
    });
    panel.querySelectorAll('[data-mismo-view]').forEach((v) => {
      if (v instanceof HTMLElement) v.hidden = v.getAttribute('data-mismo-view') !== name;
    });
    this.#place();
    this.#syncVideos();
  }

  #lock() {
    // Use the theme scroll lock when it loads. If it does not load, the page still works.
    this.#lockOwner = this;
    import('@theme/utilities')
      .then((mod) => {
        if (!this.#openPanel) return;
        this.#lockFns = mod;
        mod.lockScroll(this);
      })
      .catch(() => {});
  }

  #unlock() {
    if (this.#lockFns) {
      this.#lockFns.unlockScroll(this);
      this.#lockFns = null;
    }
    this.#lockOwner = null;
  }
}

if (!customElements.get('mismo-nav')) {
  customElements.define('mismo-nav', MismoNav);
}